<template>
  <section>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="2">
            <FormulateInput
              id="customer_email_access-id"
              v-model="filter.id"
              name="id"
              type="text-number"
              maxlength="20"
              :label="$t('Identificador')"
              :placeholder="$t('Identificador')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="customer_email_access-document"
              v-model="filter.document"
              name="document"
              type="text-mask"
              :mask="['###.###.###-##']"
              :label="$t('CPF')"
              :placeholder="$t('CPF')"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="customer_email_access-name"
              v-model="filter.name"
              name="name"
              type="text"
              maxlength="150"
              :label="$t('Nome')"
              :placeholder="$t('Nome')"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="getData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        id="customer-email-access-table"
        ref="customer-email-access-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('CUSTOMER.NAME'), 'male', queryExecuted)"
        :fields="fields"
        :items="customers"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(document)="row">
          {{ row.item.document | cpfCnpj }}
        </template>

        <template #cell(active)="row">
          <e-status-badge :status="row.item.active" />
        </template>

        <template #cell(emailAccess)="row">
          {{ row.item.emailAccess || '' }}
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :show-update="hasEmailAccess(row.item) && $can('UpdateEmailAccess', 'Customer')"
            :show-delete="false"
            @update="onEditEmailAccess(row.item)"
          />
        </template>
      </b-table>

      <b-row class="pt-1">
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="customer-email-access-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <e-modal-custom-form
      ref="modalEmailAccess"
      :title="$t('Alterar E-mail de Acesso')"
    >
      <b-row>
        <b-col
          md="12"
          class="d-flex flex-column"
        >
          <FormulateInput
            id="customer_email_access-edit-email"
            v-model="emailAccessForm.emailAccess"
            name="emailAccess"
            class="w-100 required"
            type="email"
            maxlength="254"
            validation="required|email"
            :label="$t('E-mail de Acesso')"
            :placeholder="$t('email@dominio.com')"
          />
        </b-col>
      </b-row>
    </e-modal-custom-form>
  </section>
</template>

<script>
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EFilters,
  EGridActions,
  EPageSizeSelector,
  EPaginationSummary,
  EStatusBadge,
} from '@/views/components'
import EModalCustomForm from '@/views/components/EModalCustomForm.vue'
import { mapActions, mapState } from 'vuex'

const MAX_PAGE_SIZE = 50
const CPF_DIGITS = 11
const NAME_MIN_LENGTH = 3

export default {
  name: 'CustomerEmailAccessList',

  components: {
    BRow,
    BCol,
    BPagination,
    BTable,
    BCardActions,
    EFilters,
    EGridActions,
    EPageSizeSelector,
    EPaginationSummary,
    EStatusBadge,
    EModalCustomForm,
  },

  data() {
    return {
      fetching: false,
      selectedCustomer: null,
      emailAccessForm: {
        emailAccess: '',
      },
    }
  },

  computed: {
    ...mapState('pages/security/customerEmailAccess', [
      'customers',
      'paging',
      'sorting',
      'filter',
      'queryExecuted',
    ]),

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thClass: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Identificador'),
          key: 'id',
          tdClass: 'text-right',
          thClass: 'text-center',
          thStyle: { width: '120px' },
          sortable: true,
        },
        {
          label: this.$t('CPF'),
          key: 'document',
          class: 'text-left',
          thClass: 'text-center',
          thStyle: { width: '180px' },
        },
        {
          label: this.$t('Nome'),
          key: 'name',
          class: 'text-left',
          thClass: 'text-center',
          sortable: true,
        },
        {
          label: this.$t('Ativo'),
          key: 'active',
          class: 'text-center',
          thClass: 'text-center',
          thStyle: { width: '80px' },
          sortable: true,
        },
        {
          label: this.$t('E-mail de Acesso'),
          key: 'emailAccess',
          class: 'text-left',
          thClass: 'text-center',
          thStyle: { width: '280px' },
        },
      ]
    },
  },

  beforeDestroy() {
    this.cleanState()
  },

  methods: {
    ...mapActions('pages/security/customerEmailAccess', [
      'fetchCustomers',
      'updateEmailAccess',
      'setFilter',
      'setCurrentPage',
      'setPageSize',
      'resetFilter',
      'cleanState',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchCustomers()
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    isFilterValid() {
      const document = (this.filter.document || '').replace(/\D/g, '')
      if (document && document.length !== CPF_DIGITS) {
        this.showWarning({ message: this.$t('Formato de CPF inválido') })
        return false
      }

      const name = (this.filter.name || '').trim()
      if (name && name.length < NAME_MIN_LENGTH) {
        this.showWarning({
          message: this.$t('Informe ao menos {min} caracteres para filtrar por nome', {
            min: NAME_MIN_LENGTH,
          }),
        })
        return false
      }

      return true
    },

    filterData() {
      if (!this.isFilterValid()) return

      this.setFilter(this.filter)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilter()
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(Math.min(pageSize, MAX_PAGE_SIZE))
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    hasEmailAccess(item) {
      return !!(item?.emailAccess && item.emailAccess.trim())
    },

    async onEditEmailAccess(item) {
      if (!this.hasEmailAccess(item)) return

      this.selectedCustomer = item
      this.emailAccessForm.emailAccess = item.emailAccess

      try {
        await this.$refs.modalEmailAccess.show(async () => {
          const emailAccess = (this.emailAccessForm.emailAccess || '').trim()

          // A action commita a atualização da linha na listagem somente em caso de
          // sucesso; em conflito/falha a exceção é propagada e o valor anterior é
          // preservado tanto na listagem quanto no modal.
          await this.updateEmailAccess({ id: item.id, emailAccess })

          this.showSuccess({
            message: this.$t('E-mail de acesso atualizado com sucesso'),
          })
        })
      } catch (error) {
        // Modal cancelado pelo usuário: nenhum erro a ser exibido. Falhas de
        // salvamento/conflito já são tratadas internamente pelo modal.
      } finally {
        this.selectedCustomer = null
        this.emailAccessForm.emailAccess = ''
      }
    },
  },
}
</script>

<style></style>
